import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link, useLocation } from "react-router-dom";
import api, { imgUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatRate } from "@/lib/services";
import { ArrowLeft, MapPin, Eye, Phone, MessageSquare, Clock, User, Users, Send, Share2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import SellerReviews from "@/components/SellerReviews";
import { RatingBadge } from "@/components/Stars";
import AvailableBadge from "@/components/AvailableBadge";
import VerifiedBadge from "@/components/VerifiedBadge";
import ScheduleView from "@/components/ScheduleView";

const TEMPLATES = ["Ben je vandaag beschikbaar?", "Wat zijn de mogelijkheden?", "Ontvang je ook of kom je langs?"];

function InfoTile({ icon: Icon, label, value, testid }) {
  if (!value) return null;
  return (
    <div data-testid={testid} className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4">
      <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-zinc-500"><Icon className="h-3.5 w-3.5 text-rose-400" /> {label}</div>
      <div className="mt-1.5 text-sm text-zinc-100 whitespace-pre-line">{value}</div>
    </div>
  );
}

export default function ServiceDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const counted = !!useLocation().state?.counted;
  const [s, setS] = useState(null);
  const [active, setActive] = useState(0);
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [phone, setPhone] = useState("");
  const onStats = useCallback((r) => setS((prev) => (prev ? { ...prev, seller_rating: r } : prev)), []);

  useEffect(() => {
    api.get(`/products/${id}`, { params: counted ? { count_view: false } : {} }).then(({ data }) => {
      if (data.listing_type !== "service") { navigate(`/product/${id}`, { replace: true }); return; }
      setS(data);
    }).catch(() => navigate("/diensten"));
  }, [id, navigate, counted]);

  if (!s) return <div className="mx-auto max-w-7xl px-6 py-20 text-center text-zinc-500">Laden...</div>;

  const isOwner = user && user.id === s.seller_id;
  const images = s.images || [];

  const revealPhone = async () => {
    if (!user) { navigate("/auth"); return; }
    try {
      const { data } = await api.get(`/products/${s.id}/phone`);
      setPhone(data.phone);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Nummer niet beschikbaar");
    }
  };

  const sendMessage = async () => {
    if (!user) { navigate("/auth"); return; }
    if (!msg.trim()) return;
    setSending(true);
    try {
      await api.post("/messages", { product_id: s.id, text: msg });
      toast.success("Bericht verzonden");
      navigate("/berichten");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kon bericht niet versturen");
      setSending(false);
    }
  };

  const share = async () => {
    const url = `${window.location.origin}/api/share/product/${s.id}`;
    if (navigator.share) { try { await navigator.share({ title: s.title, url }); } catch {} return; }
    await navigator.clipboard.writeText(url).catch(() => {});
    toast.success("Link gekopieerd");
  };

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-8">
      <button data-testid="service-back-button" onClick={() => navigate("/diensten")} className="flex items-center gap-2 text-sm text-zinc-400 hover:text-zinc-100 mb-6">
        <ArrowLeft className="h-4 w-4" /> Terug naar diensten
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.1fr] gap-8">
        <div>
          <div className="relative aspect-[3/4] overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-950">
            {images.length ? <img data-testid="service-main-image" src={imgUrl(images[active])} alt={s.title} className="h-full w-full object-cover" />
              : <div className="flex h-full items-center justify-center text-zinc-700 font-serif">Geen foto</div>}
          </div>
          {images.length > 1 && (
            <div className="mt-3 flex gap-3 overflow-x-auto">
              {images.map((im, i) => (
                <button key={i} data-testid={`service-thumb-${i}`} onClick={() => setActive(i)} className={`h-20 w-16 shrink-0 overflow-hidden rounded-lg border-2 ${i === active ? "border-rose-500" : "border-zinc-800"}`}>
                  <img src={imgUrl(im)} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <div className="flex flex-wrap items-center gap-3 mb-2">
            <span className="font-mono text-xs uppercase tracking-widest text-rose-400">{s.category}</span>
            <AvailableBadge available={s.available_today} testid={s.available_today ? "service-available-today-badge" : "service-unavailable-today-badge"} />
            <RatingBadge rating={s.seller_rating} testid="service-rating-badge" />
            <VerifiedBadge show={s.seller_verified} testid="service-verified-badge" />
          </div>
          <h1 data-testid="service-title" className="font-serif text-3xl sm:text-4xl font-semibold text-zinc-100 leading-tight">{s.title}</h1>
          <div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-zinc-500">
            <span className="flex items-center gap-1"><MapPin className="h-4 w-4" /> {s.location || "Onbekend"}</span>
            <span className="flex items-center gap-1"><Eye className="h-4 w-4" /> {s.views} weergaven</span>
            <button data-testid="service-share-button" onClick={share} className="flex items-center gap-1 text-rose-300 hover:text-rose-200"><Share2 className="h-4 w-4" /> Delen</button>
          </div>
          <div data-testid="service-rate" className="mt-6 font-mono text-4xl font-semibold text-rose-400">{formatRate(s)}</div>

          <div className="mt-6 grid grid-cols-2 gap-3">
            <InfoTile icon={User} label="Geslacht" value={s.gender} testid="service-gender" />
            <InfoTile icon={User} label="Leeftijd" value={s.age ? `${s.age} jaar` : ""} testid="service-age" />
            <InfoTile icon={Users} label="Voor wie" value={s.for_whom} testid="service-for-whom" />
            <InfoTile icon={Clock} label="Beschikbaarheid" value={s.availability} testid="service-availability" />
          </div>

          <ScheduleView schedule={s.schedule} />

          <div className="mt-6 rounded-xl border border-zinc-800 bg-zinc-900/40 p-5">
            <h2 className="font-serif text-xl text-zinc-200 mb-2">Over mij</h2>
            <p data-testid="service-description" className="text-sm text-zinc-400 leading-relaxed whitespace-pre-line">{s.description}</p>
          </div>

          {isOwner ? (
            <Link to={`/dienst-plaatsen/${s.id}`} data-testid="service-edit-button" className="mt-6 flex items-center justify-center rounded-lg border border-zinc-700 text-zinc-200 hover:bg-zinc-800 px-5 py-3 text-sm font-medium">Dienst bewerken</Link>
          ) : (
            <div className="mt-6 rounded-xl border border-rose-900/50 bg-rose-950/10 p-5 space-y-4">
              <div className="flex flex-wrap items-center gap-2 text-sm font-medium text-zinc-200">Neem contact op met {s.seller_name || "de aanbieder"} <VerifiedBadge show={s.seller_verified} testid="service-contact-verified-badge" /></div>
              {s.show_phone && (phone ? (
                <a data-testid="service-phone-number" href={`tel:${phone}`} className="flex items-center justify-center gap-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-mono font-semibold px-5 py-3">
                  <Phone className="h-4 w-4" /> {phone}
                </a>
              ) : (
                <button data-testid="service-reveal-phone-button" onClick={revealPhone} className="w-full flex items-center justify-center gap-2 rounded-lg border border-emerald-700/60 bg-emerald-950/30 text-emerald-300 hover:bg-emerald-950/60 font-medium px-5 py-3 transition-colors">
                  <Phone className="h-4 w-4" /> {user ? "Toon telefoonnummer" : "Log in om het nummer te zien"}
                </button>
              ))}
              <div className="flex flex-wrap gap-2">
                {TEMPLATES.map((tpl) => (
                  <button key={tpl} type="button" onClick={() => setMsg(tpl)} className="rounded-full border border-zinc-800 bg-zinc-900/60 px-3 py-1 text-xs text-zinc-400 hover:text-zinc-100">{tpl}</button>
                ))}
              </div>
              <textarea data-testid="service-message-input" rows={3} value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Schrijf een bericht..."
                className="w-full rounded-lg border border-zinc-800 bg-zinc-900/60 px-4 py-3 text-sm text-zinc-100 placeholder:text-zinc-500 focus:border-rose-700 focus:outline-none" />
              <button data-testid="service-send-message-button" onClick={sendMessage} disabled={sending}
                className="w-full flex items-center justify-center gap-2 rounded-lg bg-rose-600 hover:bg-rose-500 text-white font-medium px-5 py-3 disabled:opacity-60">
                {user ? <><Send className="h-4 w-4" /> {sending ? "Versturen..." : "Stuur bericht"}</> : <><MessageSquare className="h-4 w-4" /> Log in om te chatten</>}
              </button>
              <p className="flex items-center gap-1.5 text-xs text-zinc-500"><ShieldCheck className="h-3.5 w-3.5" /> Spreek altijd veilig af en respecteer grenzen. Betaling regel je onderling.</p>
            </div>
          )}
        </div>
      </div>
      <SellerReviews onStats={onStats} sellerId={s.seller_id} sellerName={s.seller_name} productId={s.id} />
    </div>
  );
}
